/* eslint camelcase: "off" */
/**
 * Access audit — DocumentAccessLog browsing.
 *
 * Wired to verificationClient.listAccessLog (django-monolith
 * /verification/admin/access-log/).
 *
 * Compliance-friendly: "who looked at which document, when, with what
 * reason." Required for POPIA SAR responses and internal audit reviews.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  InputField,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tag,
} from '@jem-open/jem-ui';
import { Loader2 } from 'lucide-react';

import apiResponseHandler from '../../../../../utils/apiResponseHandler';
import {
  DocumentAccessKind,
  DocumentAccessLogRow,
  verificationClient,
} from '../../../../../services/verificationClient';


const AccessAuditTab: React.FC = () => {
  const requestIdRef = useRef(0);
  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState<string>('_all');
  const [rows, setRows] = useState<DocumentAccessLogRow[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    setLoading(true);
    try {
      const params: Parameters<typeof verificationClient.listAccessLog>[0] = {};
      if (kindFilter !== '_all') params.access_kind = kindFilter as DocumentAccessKind;
      // Actor filter on the backend is an `icontains` match — we wire
      // the search box to it so the wire-side filter does most of the
      // work and the client falls back to local filtering only when
      // the API returns nothing.
      if (search) params.actor = search;
      const page = await verificationClient.listAccessLog(params);
      if (requestId !== requestIdRef.current) return;
      setRows(page.results);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      apiResponseHandler.handleApiError(err);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [search, kindFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const display: DocumentAccessLogRow[] = rows;

  return (
    <div>
      <div className="mb-sm rounded-2xl border border-greyscale-border-default bg-white p-md">
        <div className="text-xs text-greyscale-text-caption">
          Every document byte / signed-URL access is logged with an actor, reason, and ticket
          reference. Surface here for POPIA subject-access requests and internal review.
        </div>
      </div>

      {/* Filters */}
      <div className="mb-sm grid grid-cols-1 gap-sm rounded-2xl border border-greyscale-border-default bg-white p-md md:grid-cols-2">
        <InputField
          label="Search actor"
          value={search}
          placeholder="Email (substring match)"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
        />
        <div className="flex flex-col gap-xxs">
          <span className="text-xs text-greyscale-text-caption">Access kind</span>
          <Select value={kindFilter} onValueChange={setKindFilter}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All</SelectItem>
              <SelectItem value="download">Download (bytes)</SelectItem>
              <SelectItem value="signed_url">Signed URL</SelectItem>
              <SelectItem value="metadata">Metadata only</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-greyscale-border-default bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>When</TableHead>
              <TableHead>Actor</TableHead>
              <TableHead>Document</TableHead>
              <TableHead>Kind</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Ticket</TableHead>
              <TableHead>Source IP</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {display.map((a) => (
              <TableRow key={a.id}>
                <TableCell className="text-xs">
                  {new Date(a.date_created).toLocaleString()}
                </TableCell>
                <TableCell className="text-xs">{a.actor}</TableCell>
                <TableCell className="text-xs font-mono">
                  <code>{shortenUuid(a.document)}</code>
                </TableCell>
                <TableCell>
                  <Tag
                    variant={
                      a.access_kind === 'download'
                        ? 'failed'
                        : a.access_kind === 'signed_url'
                        ? 'pending'
                        : 'processing'
                    }
                  >
                    {a.access_kind}
                  </Tag>
                </TableCell>
                <TableCell className="text-xs">{a.access_reason}</TableCell>
                <TableCell className="text-xs">
                  {a.ticket_ref ? <code>{a.ticket_ref}</code> : '—'}
                </TableCell>
                <TableCell className="text-xs font-mono">{a.source_ip || '—'}</TableCell>
              </TableRow>
            ))}
            {display.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={7} className="py-md text-center text-greyscale-text-caption">
                  No access events match.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {loading && (
          <div className="flex justify-center py-sm">
            <Loader2 className="animate-spin text-secondary-text-label" size={20} />
          </div>
        )}
      </div>
    </div>
  );
};


function shortenUuid(uuid: string): string {
  // First 8 + last 4 — enough to disambiguate by eye, fits in one column.
  if (uuid.length < 14) return uuid;
  return `${uuid.slice(0, 8)}…${uuid.slice(-4)}`;
}

export default AccessAuditTab;
